/*
 * Wishlist Page Render — draws the wishlist page grid from BroraWishlist data.
 * Adapts each wishlist item into the shape window.renderVoyadoCard expects
 * (assets/voyado-card-renderer.js) so the page reuses the PLP card itself rather
 * than carrying a second copy of its slot-filling. On `brora:wishlist:changed`,
 * reconciles cards keyed by item id into [data-wishlist-page-grid].
 * View only — never touches window.WishlistKing.
 */
;(function () {
  'use strict';

  // Wishlist money is in cents; the card renderer formats major units.
  function major(cents) {
    return (Number(cents) || 0) / 100;
  }

  // Wishlist item → the product shape voyado-card-renderer.js reads. The wishlist feed carries no
  // colour siblings, badges or stock numbers, so those slots collapse to their own empty states.
  function toCardProduct(item) {
    const product = item.product || {};
    const url = product.url || (product.handle ? '/products/' + product.handle : '');
    let images = product.images || [];
    if (!images.length && product.featured_image) images = [product.featured_image];

    const priceMin = major(product.price_min);
    const priceMax = major(product.price_max);
    const compareMin = major(product.compare_at_price_min);
    const compareMax = major(product.compare_at_price_max);

    return {
      key: String(item.product_id != null ? item.product_id : (product.id || '')),
      title: product.title || '',
      link: url,
      ticket: '',
      imageInfo: { images: images.map(function (src) { return { sources: [{ url: src }] }; }) },
      sellingPrice: { min: priceMin, max: priceMax },
      listPrice: compareMin > priceMin ? { min: compareMin, max: compareMax } : null,
      variants: (product.variants || []).map(function (v) {
        // Shopify spells the single-variant placeholder "Default Title"; quick-add tests for the
        // feed's spelling, "Default", to render one "One Size" pill.
        const label = v.option1 === 'Default Title' ? 'Default' : (v.option1 || '');
        return {
          link: url + '?variant=' + v.id,
          inStock: !!v.available,
          label: label,
          ticket: ''
        };
      })
    };
  }

  // How long a removed card stays undoable before the removal is committed. Matches the drawer.
  const REMOVE_UNDO_MS = 3000;
  const pending = new Map();
  // True once the page is showing a list the reader does not own.
  let sharedList = false;
  // A shared link paints nothing until ownership is settled: the drawer primes the client on load,
  // and its event carries the READER's list.
  let ownershipResolved = true;

  function clone(name) {
    const t = document.querySelector('[data-tmpl-' + name + ']');
    const node = t && t.content && t.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  }

  function announce(message) {
    const el = document.querySelector('[data-wishlist-page-announcer]');
    if (el) el.textContent = message;
  }

  // Removal is deferred: the API call fires when the undo window expires, so an undo is a
  // cancelled timer rather than a second write.
  function markRemoved(card, itemId) {
    card.setAttribute('data-wishlist-page-item-state', 'removed');
    const host = document.querySelector('[data-main-wishlist]');
    announce((host && host.getAttribute('data-item-removed-message')) || '');
    pending.set(card, setTimeout(function () {
      pending.delete(card);
      if (window.BroraWishlist) window.BroraWishlist.removeItem(itemId).catch(function () {});
    }, REMOVE_UNDO_MS));
  }

  function undoRemoval(card) {
    const timer = pending.get(card);
    if (timer) clearTimeout(timer);
    pending.delete(card);
    card.removeAttribute('data-wishlist-page-item-state');
    announce('');
  }

  // Everything here is already saved, so the heart's toggle has nothing to say: it gives way to the
  // drawer's bin, with the undo taking the row the bin leaves.
  function swapHeartForActions(card, itemId) {
    const heart = card.querySelector('[data-slot="wishlist"]');
    const actions = clone('wishlist-page-actions');
    if (!heart || !actions) return;
    // The heart is the card's only carrier of data-wishlist-product-id, which is how the Storefront
    // price corrector identifies a card.
    const productId = heart.getAttribute('data-wishlist-product-id');
    if (productId) actions.setAttribute('data-wishlist-product-id', productId);
    heart.replaceWith(actions);
    const bin = actions.querySelector('[data-wishlist-page-remove]');
    if (bin) bin.addEventListener('click', function () { markRemoved(card, itemId); });
  }

  // Keyed reconcile: reuse card nodes already on the page, add new, drop gone, in list order.
  function reconcile(grid, list) {
    if (typeof window.renderVoyadoCard !== 'function') return;
    const existing = {};
    grid.querySelectorAll('[data-wishlist-page-item-id]').forEach(function (n) {
      existing[n.getAttribute('data-wishlist-page-item-id')] = n;
    });
    const wanted = {};
    let prev = null;
    list.forEach(function (item, index) {
      const id = String(item.id);
      wanted[id] = true;
      let node = existing[id];
      if (!node) {
        node = window.renderVoyadoCard(toCardProduct(item), { quickAdd: true, priority: index === 0 });
        node.setAttribute('data-wishlist-page-item-id', id);
        // On someone else's shared list the heart stays: it is the reader's own save action, and
        // removal belongs to the owner alone.
        if (!sharedList) swapHeartForActions(node, item.id);
      }
      const ref = prev ? prev.nextSibling : grid.firstChild;
      if (node !== ref) grid.insertBefore(node, ref);
      prev = node;
    });
    Object.keys(existing).forEach(function (id) {
      if (!wanted[id]) existing[id].remove();
    });
  }

  function setState(host, mode) {
    host.setAttribute('data-wishlist-page-state', mode);
    const grid = host.querySelector('[data-wishlist-page-grid]');
    const empty = host.querySelector('[data-wishlist-page-empty]');
    const error = host.querySelector('[data-wishlist-page-error]');
    const loggedOut = host.querySelector('[data-wishlist-page-logged-out]');
    if (grid) grid.classList.toggle('hidden', mode !== 'items');
    if (empty) empty.classList.toggle('hidden', mode !== 'empty');
    if (error) error.classList.toggle('hidden', mode !== 'error');
    // Nothing saved yet, or someone else's list: either way the invitation makes no sense.
    if (loggedOut) {
      const showBanner = mode === 'items' && !sharedList;
      loggedOut.classList.toggle('hidden', !showBanner);
      loggedOut.classList.toggle('flex', showBanner);
    }
    // Share follows the same rule: an empty list has nothing to share, and someone else's is not
    // the reader's to pass on.
    const share = host.querySelector('[data-wishlist-page-share-wrap]');
    if (share) share.classList.toggle('hidden', !(mode === 'items' && !sharedList));
  }

  // Reuse the drawer's share layer rather than a second one: open the drawer, then slide it in.
  function bindShare(host) {
    const btn = host.querySelector('[data-wishlist-page-share]');
    if (!btn || btn.dataset.bound === 'true') return;
    btn.dataset.bound = 'true';
    let openedFromPage = false;

    // Opened from the page, the drawer is scaffolding the shopper never asked for, so closing share
    // takes it too. The layer fires no close event; its state attribute is the seam.
    const drawerEl = document.querySelector('[data-wishlist-drawer]');
    if (drawerEl) {
      // The panel leaves with the layer still over it. Letting the layer go first would uncover the
      // wishlist for the panel's whole transition — a list the shopper never opened, on its way out.
      drawerEl.addEventListener('click', function (e) {
        if (!openedFromPage || !e.target.closest('[data-wishlist-drawer-share-close]')) return;
        const layer = drawerEl.querySelector('[data-wishlist-drawer-share-layer]');
        const drawer = window.broraWishlistDrawer;
        if (!layer || !drawer) return;
        e.stopPropagation(); // the layer's own close would strip it mid-exit
        openedFromPage = false;
        const panel = drawerEl.querySelector('[role="dialog"]');
        layer.style.transitionDuration = '0s';
        layer.style.translate = '0px';
        drawer.close(); // closeDrawer resets the layer's state; the inline offset outlives it
        // The panel holds still for its own closing delay before it travels, so release the layer on
        // the panel's transition rather than a guessed timeout — either would otherwise uncover the list.
        const release = function () {
          layer.style.translate = '';
          layer.style.transitionDuration = '';
          drawerEl.removeAttribute('data-wishlist-drawer-share-only');
        };
        if (panel) {
          panel.addEventListener('transitionend', function once(ev) {
            if (ev.target !== panel || ev.propertyName !== 'translate') return;
            panel.removeEventListener('transitionend', once);
            release();
          });
        } else {
          release();
        }
      }, true);

      // Any other route out — the drawer's own X, Escape, the overlay — still takes both.
      new MutationObserver(function () {
        if (!openedFromPage || drawerEl.getAttribute('data-wishlist-drawer-share') !== 'closed') return;
        openedFromPage = false;
        drawerEl.removeAttribute('data-wishlist-drawer-share-only');
        const drawer = window.broraWishlistDrawer;
        if (drawer && drawer.isOpen()) drawer.close();
      }).observe(drawerEl, { attributes: true, attributeFilter: ['data-wishlist-drawer-share'] });
    }

    btn.addEventListener('click', function () {
      const drawer = window.broraWishlistDrawer;
      const shareLayer = window.BroraWishlistDrawerShare;
      if (!drawer || !shareLayer) return;
      openedFromPage = true;
      // Hide the list behind the layer (see _wishlist-page.css) rather than freezing the layer over
      // it: with nothing to reveal, both keep their own transitions, which on mobile run opposite ways.
      if (drawerEl) drawerEl.setAttribute('data-wishlist-drawer-share-only', 'true');
      if (!drawer.isOpen()) drawer.open(btn);
      shareLayer.open(btn);
    });
  }

  // The app returns the whole list in one response, with no cursor, so paging is ours: a page at a
  // time, growing on scroll, as the PLP does.
  const PAGE_SIZE = 24;
  let fullList = [];
  let shown = 0;
  let paginator = null;

  function syncSentinel(host, grid) {
    const existing = host.querySelector('[data-wishlist-page-load-more]');
    if (shown >= fullList.length) {
      if (existing) existing.remove();
      if (paginator) { paginator.destroy(); paginator = null; }
      return;
    }
    if (!existing) {
      const tmpl = document.querySelector('[data-tmpl-wishlist-page-load-more]');
      const node = tmpl && tmpl.content.firstElementChild.cloneNode(true);
      if (!node) return;
      grid.after(node);
    }
    if (typeof window.createVoyadoPagination !== 'function') return;
    // Re-observe after every page. An observer reports only a CHANGE of intersection, so a page that
    // leaves the sentinel in view would fire nothing further and strand the tail of the list.
    if (paginator) paginator.destroy();
    paginator = window.createVoyadoPagination({
      root: host,
      sentinelSelector: '[data-wishlist-page-load-more]',
      onLoadMore: function () {
        shown = Math.min(shown + PAGE_SIZE, fullList.length);
        paint(host, grid);
      }
    });
  }

  function paint(host, grid) {
    reconcile(grid, fullList.slice(0, shown));
    // The seam every card inserter uses: binds gallery, quick-add and video behaviour, and runs the
    // Storefront price correction.
    document.dispatchEvent(new CustomEvent('voyado:cards-rendered', { detail: { root: grid } }));
    syncSentinel(host, grid);
  }

  function render(detail) {
    const host = document.querySelector('[data-wishlist-page]');
    if (!host) return;
    const grid = host.querySelector('[data-wishlist-page-grid]');
    fullList = (detail && detail.items) || [];
    // Hold what is already on screen through a removal, rather than collapsing to the first page.
    shown = Math.min(Math.max(shown || PAGE_SIZE, PAGE_SIZE), fullList.length);
    if (grid) paint(host, grid);
    setState(host, fullList.length > 0 ? 'items' : 'empty');
  }

  // A shared list is read by its publicId, carried either as ?list= on this page or as the trailing
  // segment of the app's own /apps/wishlist/<publicId> route.
  // The id reaches the app SDK, which builds a request carrying the session header, so both forms
  // are validated.
  const ID_PATTERN = /^[A-Za-z0-9_-]+$/;
  function sharedIdFromUrl() {
    const fromQuery = (new URLSearchParams(location.search).get('list') || '').trim();
    if (fromQuery) return ID_PATTERN.test(fromQuery) ? fromQuery : null;
    const m = location.pathname.match(/\/apps\/wishlist\/([A-Za-z0-9_-]+)\/?$/);
    return m ? m[1] : null;
  }

  // "<Name>'s Wishlist" when the sharer was signed in, "Shared Wishlist" otherwise. The name arrives
  // from the link, so it is untrusted: textContent only, length-capped.
  function titleSharedPage() {
    const heading = document.querySelector('[data-wishlist-page-title]');
    if (!heading) return;
    const name = (new URLSearchParams(location.search).get('from') || '').trim().slice(0, 40);
    const named = heading.getAttribute('data-title-shared-named') || '';
    // Function replacement, so a name holding $& or $1 inserts literally.
    heading.textContent = name && named
      ? named.replace('__NAME__', function () { return name; })
      : (heading.getAttribute('data-title-shared') || heading.textContent);
  }

  document.addEventListener('brora:wishlist:changed', function (e) {
    // Until ownership is settled, and afterwards whenever a foreign list is on screen, this event
    // belongs to the reader's OWN list and must not paint over the list being read.
    if (!ownershipResolved || sharedList) return;
    render(e.detail);
  });

  // Delegated so a card rebuilt by a later reconcile keeps working.
  document.addEventListener('click', function (e) {
    const button = e.target.closest('[data-wishlist-page-undo-button]');
    if (!button) return;
    const card = button.closest('[data-wishlist-page-item-id]');
    if (card) undoRemoval(card);
  });

  // The section loads this ahead of the globals in layout/theme.liquid, so the client does not exist
  // at parse time. Poll for it rather than lean on another consumer priming it first.
  function whenClientReady(callback) {
    const started = Date.now();
    (function poll() {
      if (window.BroraWishlist) return callback(window.BroraWishlist);
      if (Date.now() - started > 8000) return callback(null);
      setTimeout(poll, 150);
    })();
  }

  const pageHost = document.querySelector('[data-wishlist-page]');
  if (pageHost) {
    bindShare(pageHost);
    // Read the id and close the gate synchronously, before any await.
    const publicId = sharedIdFromUrl();
    if (publicId) ownershipResolved = false;
    whenClientReady(function (client) {
      if (!client) { ownershipResolved = true; setState(pageHost, 'error'); return; }
      if (publicId && client.loadShared) {
        client.loadShared(publicId).then(function (res) {
          if (!res) { ownershipResolved = true; setState(pageHost, 'error'); return; }
          // The owner opening their own link gets their own page, actions and title.
          sharedList = !res.isMine;
          ownershipResolved = true;
          pageHost.setAttribute('data-wishlist-page-shared', String(sharedList));
          if (sharedList) titleSharedPage();
          render({ items: res.items });
        }).catch(function () { ownershipResolved = true; setState(pageHost, 'error'); });
      } else {
        ownershipResolved = true;
        client.getItems().catch(function () { setState(pageHost, 'error'); });
      }
    });
  }
})();
